export const adminFinance = {
  seo: {
    title: "Admin & finance — Bespire AI assistant",
    description:
      "Business overview, monthly close, performance reports, capacity and cost planning. Bespire AI helps you see your business clearly.",
  },
  hero: {
    badge: "Admin & finance",
    title: "See your business clearly — monthly close as one conversation",
    subtitle:
      "Business overview, capacity planning, revenue and cost reports. Bespire AI works on the numbers daily so you can focus on your craft. The system handles online and in-person payments itself; Bespire does not do invoicing.",
    ctaTrial: "Try it free for 3 days",
    ctaPricing: "See pricing →",
    dashboard: {
      title: "This week's business close",
      subtitle: "Week 24 · Jun 8–14, 2026",
      kpis: [
        { label: "Revenue", value: "€1,340", delta: "+11%", positive: true },
        { label: "Bookings", value: "47", delta: "+3", positive: true },
        { label: "Capacity used", value: "78%", delta: "+6%", positive: true },
        { label: "Expenses", value: "€240", delta: "categorized", positive: true },
      ],
      chartLabel: "Daily revenue",
      chartDays: ["M", "T", "W", "T", "F", "S", "S"],
      chartValues: [64, 82, 51, 96, 108, 88, 39],
      chartUnit: "€10",
      aiNote: "Wednesday utilization is lower — a group-class proposal is ready.",
    },
  },
  paperwork: {
    title: "The paperwork that just disappears from your list",
    subtitle: "Same work — you just don't do it.",
    before: {
      label: "What you used to do",
      items: [
        "Excel sheets to track revenue",
        "Excel sheets to categorize expenses",
        "Manual capacity planning in the calendar",
        "Collecting monthly numbers for decisions",
        "Remembering who to call back and when",
        "Separate tools for your accountant",
      ],
    },
    after: {
      label: "What Bespire does for you",
      items: [
        "Weekly and monthly revenue reports with AI commentary",
        "Auto-categorize expenses from uploaded receipts",
        "Capacity analysis and empty-slot alerts",
        "Monthly close package in one click",
        "It knows who was messaged, when, and how many times",
        "Accountant export as PDF and Excel",
      ],
    },
  },
  lifecycle: {
    title: "The life of one booking — from reservation to revenue",
    subtitle: "From booking to close — each step is either automatic, or one click.",
    steps: [
      { icon: "Calendar", label: "Booking", description: "Client books a slot" },
      { icon: "CreditCard", label: "Payment", description: "Online or in-person inside the system" },
      { icon: "Eye", label: "Track", description: "Live payment status" },
      { icon: "Bell", label: "Reminder", description: "Automatic on overdue" },
      { icon: "BarChart3", label: "Report", description: "Weekly / monthly close in one package" },
    ],
  },
  mocks: {
    title: "What the outputs actually look like",
    subtitle: "Two real business artifacts: a weekly overview and a monthly close report.",
    invoice: {
      badge: "Weekly overview",
      number: "Week 24",
      date: "Jun 8–14, 2026",
      due: "",
      fromLabel: "Period",
      from: "Jun 8–14, 2026",
      toLabel: "Summary",
      to: "Kata Recovery",
      items: [
        { label: "Total bookings", amount: "47" },
        { label: "Online payments", amount: "€920" },
        { label: "In-person payments", amount: "€420" },
      ],
      totalLabel: "Weekly revenue",
      total: "€1,340",
      status: "Settled",
    },
    report: {
      badge: "Monthly close (sample)",
      week: "June 2026",
      kpis: [
        { label: "Revenue", value: "€4,730", delta: "+12%" },
        { label: "Capacity used", value: "78%", delta: "+6%" },
        { label: "New clients", value: "18", delta: "+5" },
      ],
      aiCommentTitle: "AI commentary",
      aiComment: "Monthly revenue up 12%. Wednesday morning utilization is declining — proposal: 10% off morning package, targeted WhatsApp campaign to 45 lapsed clients.",
      actionLabel: "Build plan",
    },
  },
  chat: {
    title: "Monthly close as one conversation",
    subtitle: "You don't even open a spreadsheet.",
    chat: {
      title: "Admin assistant",
      online: "Online",
      messages: [
        { role: "user" as const, content: "Let's close the month. Send it to the accountant." },
        { role: "assistant" as const, content: "Done, here's the package:", list: [
          "📊 Revenue: €4,730 (28 days)",
          "💳 42 bookings — online and in-person payments",
          "📎 PDF summary + Excel attached for the accountant",
        ] },
        { role: "user" as const, content: "Email it and set it up for next month too." },
        { role: "assistant" as const, content: "Sent to info@accountant.co. Automatic monthly send scheduled for the 5th. ✅" },
      ],
    },
  },
  integrations: {
    title: "Connects to your existing flow",
    subtitle: "Bespire complements your accountant and financial routines — it doesn't replace them.",
    items: [
      { label: "Accountant email", type: "Monthly close" },
      { label: "Bank export (CSV)", type: "Expenses" },
      { label: "Google Sheets", type: "Report export" },
    ],
  },
  extras: {
    title: "Why it works",
    items: [
      { icon: "Puzzle", title: "Complements, doesn't replace", description: "Works with your existing accounting and financial routines." },
      { icon: "Clock", title: "30–60 minutes of admin a day", description: "saved — you focus on the craft." },
      { icon: "Shield", title: "GDPR, EU servers", description: "Your customer and financial data stays inside the EU." },
      { icon: "MessageSquare", title: "Local rules", description: "VAT, invoicing rules, local formats — it knows the context." },
    ],
  },
  crossLinks: {
    title: "Admin doesn't stand alone",
    subtitle: "The same AI that tracks your numbers also knows your bookings, customers and marketing.",
  },
  cta: {
    title: "Get the paperwork off your plate",
    subtitle: "3-day free trial, no card required.",
    primary: "Try it free for 3 days",
    secondary: "See pricing",
  },
};
